<script setup lang="ts">
import RcDisplaySerialNumber from '@/components/rc/common/RcDisplaySerialNumber.vue'
import MinersConsolidationDialog from '@/components/rc/miner-repairs/consolidation/MinersConsolidationDialog.vue'
import MinerActionsDialog from '@/components/rc/work-order-dashboard/MinerActionsDialog.vue'
import CopyButton from '@/components/common/CopyButton.vue'

import { useWorkOrder } from '@/composables/rc/work-order'
import type { TableColumnCtx } from 'element-plus'
import { getAllMinerStatuses, getAllPartStatuses } from '@/services/rc.service'
import { getWorkOrderDashboardMiners, getWorkOrderDashboardMinersExportCSV } from '@/services/rc/work-orders.service'
import { useOIDCStore } from '@/stores/oidc.store'
import type { IWorkOrderDashboardMinersFilters } from '@/types/rc/filters'
import { MinerBulkActions } from '@/types/rc/miners/BulkMinerActions'
import type { IService, IWorkOrderDashboardMiner, NumberedPartState } from '@/types/rc/work-orders'
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { Search } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { useQuery } from '@tanstack/vue-query'
import { watchDebounced } from '@vueuse/core'
import { useRouteQuery } from '@vueuse/router'
import { storeToRefs } from 'pinia'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { MinerShippingStatus } from '@/types/rc/miners/MinerShippingStatus'
import { NotifyFail } from '@/helpers/core/notifications'
import type { IMinerModel } from '@/types/rc/miners'
import { ShipmentType } from '@/types/rc/shipments'
import { downloadFile } from '@/util'
import { AdminPortalAPIError } from '@/types/shared/errors'

const oidcStore = useOIDCStore()
const { isRcAppManagerUser } = storeToRefs(oidcStore)

const router = useRouter()

const emit = defineEmits(['clearFilters'])

const props = defineProps<{
  number: string
  scopeOfWork: IService[]
  presetFilters: Partial<IWorkOrderDashboardMinersFilters>
}>()

const isLoadingExport = ref<boolean>(false)

const { isManageableByCurrentUser, workOrderDashboardSummaryQuery, workOrderContainersQuery } = useWorkOrder(
  props.number
)
const {
  data: woSummary,
  isError: woSummaryIsError,
  isPending: woSummaryIsPending,
  error: woSummaryError,
} = workOrderDashboardSummaryQuery()

// Fetch all containers
const containersQuery = workOrderContainersQuery({})
const { data: containersData } = containersQuery

// Filter containers by kind
const inboundContainersData = computed(
  () => containersData.value?.filter((container) => container.kind === ShipmentType.Inbound) || []
)

const outboundContainersData = computed(
  () => containersData.value?.filter((container) => container.kind === ShipmentType.Outbound) || []
)

const selectedMiners = ref<
  {
    id: number
    model: string
    sn: string
  }[]
>([])
const selectedMinerBulkAction = ref<MinerBulkActions>()
const minerActionsDialogOpen = ref<boolean>(false)
const selectedMinersAmount = computed<number>(() =>
  selectedMiners.value.length > 0 ? selectedMiners.value.length : (data.value?.total_records ?? 0)
)

// Consolidation Dialog refs
const minerConsolidationDialogOpen = ref<boolean>(false)
const minerPreset1ID = ref<number | undefined>()
const minerPreset2ID = ref<number | undefined>()
const minerPreset3ID = ref<number | undefined>()

// TODO: I think this would be better as a duo of params (req. BE change):
// - Part type
// - Part status
// e.g. send partType=CB&partStatus=Good
// Plus, implementation of a partType param would be useful.
const PsuStatus = 'psu_status'
const CbStatus = 'cb_status'
const HbStatus = 'hb_status'
const FanStatus = 'fan_status'
const UndiagnosedPartStatus = 'Undiagnosed'

const partFilterOptions = [
  {
    key: 'PSU',
    value: PsuStatus,
  },
  {
    key: 'CB',
    value: CbStatus,
  },
  {
    key: 'HB',
    value: HbStatus,
  },
  {
    key: 'FAN',
    value: FanStatus,
  },
]

const pageSearchParam = useRouteQuery<number | undefined>('page', undefined, { transform: Number })
if (pageSearchParam.value && pageSearchParam.value < 1) pageSearchParam.value = undefined

const perPageSearchParam = useRouteQuery<number | undefined>('per_page', undefined, { transform: Number })
if (perPageSearchParam.value && !DEFAULT_PAGE_SIZES.includes(perPageSearchParam.value))
  perPageSearchParam.value = undefined

const fragmentSearchParam = useRouteQuery<string | undefined>('fragment')
const minerModelsSearchParam = useRouteQuery<string | undefined>('miner_models')
const minerStatusSearchParam = useRouteQuery<string | undefined>('miner_status')
const minerShippingStatusSearchParam = useRouteQuery<string | undefined>('miner_shipping_status')
const minerMissingServiceSearchParam = useRouteQuery<string | undefined>('missing_service')
const psuStatusSearchParam = useRouteQuery<string | undefined>('psu_status')
const cbStatusSearchParam = useRouteQuery<string | undefined>('cb_status')
const hbStatusSearchParam = useRouteQuery<string | undefined>('hb_status')
const fanStatusSearchParam = useRouteQuery<string | undefined>('fan_status')
const inboundContainerSearchParam = useRouteQuery<string | undefined>('inbound_container')
const outboundContainerSearchParam = useRouteQuery<string | undefined>('outbound_container')

const partStatusSearchParam = computed(() => {
  if (psuStatusSearchParam.value) return { qpName: PsuStatus, qpValue: psuStatusSearchParam.value }
  if (cbStatusSearchParam.value) return { qpName: CbStatus, qpValue: cbStatusSearchParam.value }
  if (hbStatusSearchParam.value) return { qpName: HbStatus, qpValue: hbStatusSearchParam.value }
  if (fanStatusSearchParam.value) return { qpName: FanStatus, qpValue: fanStatusSearchParam.value }
  return null
})

const minerStatusFilterOptions = ref<string[]>([])
const minerShippingStatusFilterOptions = ref<string[]>([
  MinerShippingStatus.UnassignedToOutboundContainer,
  MinerShippingStatus.AssignedToOutboundContainer,
  MinerShippingStatus.Shipped,
  MinerShippingStatus.Delivered,
])
const partStatusFilterOptions = ref<string[]>([UndiagnosedPartStatus])
const inboundContainerFilterOptions = ref<string[]>([])
const outboundContainerFilterOptions = ref<string[]>([])
const partFilter = ref<string | undefined>(partStatusSearchParam.value?.qpName)
const partStatusFilter = ref<string | undefined>(partStatusSearchParam.value?.qpValue)
const fragmentFilter = ref<string | undefined>(fragmentSearchParam.value)
const minerModelsFilter = ref<string | undefined>(minerModelsSearchParam.value)

const selectedMinerModels = ref<IMinerModel[]>([])

// Control More Filters popover visibility
const moreFiltersVisible = ref(false)

// Populate container filter options from API data
watch(
  inboundContainersData,
  (data) => {
    if (data) {
      inboundContainerFilterOptions.value = data.map((container) => container.number)
    }
  },
  { immediate: true }
)

watch(
  outboundContainersData,
  (data) => {
    if (data) {
      outboundContainerFilterOptions.value = data.map((container) => container.number)
    }
  },
  { immediate: true }
)

watchDebounced(
  fragmentFilter,
  () => {
    if (fragmentFilter.value === undefined || fragmentFilter.value === '') {
      filters.fragment = undefined
    } else if (fragmentFilter.value.length >= 3) {
      filters.fragment = fragmentFilter.value
    }
    fragmentSearchParam.value = filters.fragment
  },
  { debounce: 500, maxWait: 1000 }
)

const filters = reactive<IWorkOrderDashboardMinersFilters>({
  page: pageSearchParam.value || 1,
  per_page: perPageSearchParam.value || DEFAULT_PAGE_SIZES[2],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  miner_models: minerModelsFilter.value,
  fragment: fragmentFilter.value,
  miner_status: minerStatusSearchParam.value,
  miner_shipping_status: minerShippingStatusSearchParam.value,
  missing_service: minerMissingServiceSearchParam.value,
  psu_status: psuStatusSearchParam.value,
  cb_status: cbStatusSearchParam.value,
  hb_status: hbStatusSearchParam.value,
  fan_status: fanStatusSearchParam.value,
  inbound_container_number: inboundContainerSearchParam.value,
  outbound_container_number: outboundContainerSearchParam.value,
})

/**
 * Clears all part status search params.
 */
const clearPartStatusSearchParams = () => {
  psuStatusSearchParam.value = undefined
  cbStatusSearchParam.value = undefined
  hbStatusSearchParam.value = undefined
  fanStatusSearchParam.value = undefined
}

const cleanPartFilters = () => {
  filters.psu_status = undefined
  filters.cb_status = undefined
  filters.hb_status = undefined
  filters.fan_status = undefined

  clearPartStatusSearchParams()
}

/**
 * Clears all search params (other than per page).
 */
const clearAllFilterSearchParams = () => {
  pageSearchParam.value = undefined
  minerStatusSearchParam.value = undefined
  minerMissingServiceSearchParam.value = undefined
  minerShippingStatusSearchParam.value = undefined
  fragmentSearchParam.value = undefined
  minerModelsSearchParam.value = undefined
  inboundContainerSearchParam.value = undefined
  outboundContainerSearchParam.value = undefined

  clearPartStatusSearchParams()
}

/**
 * Clears all filters and search params, resets pagination.
 */
const clearAllFilters = () => {
  filters.page = 1
  filters.fragment = undefined
  filters.miner_status = undefined
  filters.miner_models = undefined
  filters.missing_service = undefined
  filters.miner_shipping_status = undefined
  filters.psu_status = undefined
  filters.cb_status = undefined
  filters.hb_status = undefined
  filters.fan_status = undefined
  filters.inbound_container_number = undefined
  filters.outbound_container_number = undefined

  selectedMinerModels.value = []
  partStatusFilter.value = undefined
  fragmentFilter.value = undefined
  minerModelsFilter.value = undefined
  partFilter.value = undefined

  emit('clearFilters')

  // Page is reset, but per page isn't
  pageSearchParam.value = undefined

  clearAllFilterSearchParams()
}

watch(props.presetFilters, () => {
  if (props.presetFilters) {
    cleanPartFilters()

    Object.assign(filters, props.presetFilters)

    // Sync with local buffers
    if (filters.psu_status) {
      partFilter.value = PsuStatus
      partStatusFilter.value = filters.psu_status
      handlePartStatusFilterChange(filters.psu_status)
    } else if (filters.cb_status) {
      partFilter.value = CbStatus
      partStatusFilter.value = filters.cb_status
      handlePartStatusFilterChange(filters.cb_status)
    } else if (filters.fan_status) {
      partFilter.value = FanStatus
      partStatusFilter.value = filters.fan_status
      handlePartStatusFilterChange(filters.fan_status)
    } else if (filters.hb_status) {
      partFilter.value = HbStatus
      partStatusFilter.value = filters.hb_status
      handlePartStatusFilterChange(filters.hb_status)
    } else {
      partFilter.value = undefined
      partStatusFilter.value = undefined
    }

    if (!props.presetFilters.missing_service) filters.missing_service = undefined
    handleMissingServicesFilterChange(props.presetFilters.missing_service)

    if (!props.presetFilters.miner_status) filters.miner_status = undefined
    handleMinerStatusFilterChange(props.presetFilters.miner_status)
  }
})

const { isFetching, isError, isRefetching, data, error, refetch } = useQuery({
  queryKey: ['work-order-dashboard-miners', props.number, filters],
  queryFn: async () =>
    await getWorkOrderDashboardMiners(props.number, filters).then((data) => {
      // Handle page greater than total pages (possible from query)
      if (filters.page && filters.page > data.total_pages) {
        pageSearchParam.value = undefined
        filters.page = 1
      }
      return data
    }),
})

const hasFilters = computed(() => {
  const {
    miner_status,
    miner_shipping_status,
    missing_service,
    psu_status,
    cb_status,
    hb_status,
    fan_status,
    miner_models,
    inbound_container_number,
    outbound_container_number,
  } = filters
  return (
    !!miner_status ||
    !!miner_shipping_status ||
    !!missing_service ||
    !!psu_status ||
    !!cb_status ||
    !!hb_status ||
    !!fan_status ||
    !!fragmentFilter.value ||
    !!partFilter.value ||
    !!miner_models ||
    !!inbound_container_number ||
    !!outbound_container_number
  )
})

// Compute message to display when table is empty.
const tableEmptyText = computed(() => {
  if (isFetching.value) {
    return 'Loading...'
  } else {
    return 'No data'
  }
})

const onRowClick = (row: IWorkOrderDashboardMiner, column: TableColumnCtx<IWorkOrderDashboardMiner> | null) => {
  if (column?.type !== 'selection' && isManageableByCurrentUser.value) openMiner(row.id)
}

/**
 * Handle page change, update "page" search param.
 */
const handlePageChange = () => {
  pageSearchParam.value = filters.page > 1 ? filters.page : undefined
}

/**
 * Handle page size change, update "per_page" search param.
 */
const handlePageSizeChange = () => {
  perPageSearchParam.value = filters.per_page === DEFAULT_PAGE_SIZES[2] ? undefined : filters.per_page
}

/**
 * Opens miner's repair view in a new tab given an ID.
 *
 * @param minerId ID of miner to navigate to.
 */
const openMiner = (minerId: number) => {
  const route = router.resolve({
    name: 'rc-miner-repairs',
    params: { minerId: minerId },
  })
  window.open(route.href, '_blank')
}

/**
 * Handles miner status filter change, updates router search param.
 *
 * @param value new miner status.
 */
const handleMinerStatusFilterChange = (value?: string) => {
  minerStatusSearchParam.value = value
}

/**
 * Handles miner shipping status filter change, updates router search param.
 *
 * @param value new miner status.
 */
const handleMinerShippingStatusFilterChange = (value: string) => {
  minerShippingStatusSearchParam.value = value
}

/**
 * Handles miner missing service filter change, updates router search param.
 *
 * @param value new miner status.
 */
const handleMissingServicesFilterChange = (value?: string) => {
  minerMissingServiceSearchParam.value = value
}

/**
 * Handles inbound container filter change, updates router search param.
 *
 * @param value new inbound container.
 */
const handleInboundContainerFilterChange = (value?: string) => {
  inboundContainerSearchParam.value = value
}

/**
 * Handles outbound container filter change, updates router search param.
 *
 * @param value new outbound container.
 */
const handleOutboundContainerFilterChange = (value?: string) => {
  outboundContainerSearchParam.value = value
}

const handlePartFilterChange = (value: string) => {
  cleanPartFilters()
  if (value) {
    if (partStatusFilter.value) handlePartStatusFilterChange(partStatusFilter.value)
  } else {
    partStatusFilter.value = undefined
  }
}

/**
 * Handles part status filter change. Updates query params accordingly.
 *
 * TODO: this could be greatly simplified by simplifying the query params on this endpoint
 * per the proposed change earlier in this file.
 *
 * @param value new part status filter value.
 */
const handlePartStatusFilterChange = (value: string) => {
  switch (partFilter.value) {
    case PsuStatus:
      filters.psu_status = value
      psuStatusSearchParam.value = value
      break
    case CbStatus:
      filters.cb_status = value
      cbStatusSearchParam.value = value
      break
    case HbStatus:
      filters.hb_status = value
      hbStatusSearchParam.value = value
      break
    case FanStatus:
      filters.fan_status = value
      fanStatusSearchParam.value = value
      break
    default:
      cleanPartFilters()
  }
}

const handleSelectionChange = (selection: IWorkOrderDashboardMiner[]) => {
  selectedMiners.value = selection.map((miner) => ({
    id: miner.id,
    model: miner.model,
    sn: miner.sn,
  }))
}

const handleMinerModelSelectChange = (val?: IMinerModel[]) => {
  if (!val || val.length === 0) {
    filters.miner_models = undefined
    minerModelsSearchParam.value = undefined
    return
  }

  const models = val.map((res: IMinerModel) => res.name).join(',')
  filters.miner_models = models
  minerModelsSearchParam.value = models
}

/**
 * Handle miner actions dropdown command.
 *
 * @param command selected command.
 */
const handleMinerActions = (command: MinerBulkActions) => {
  if (
    command === MinerBulkActions.Consolidate &&
    selectedMiners.value.length >= 2 &&
    selectedMiners.value.length <= 3
  ) {
    minerConsolidationDialogOpen.value = true
    minerPreset1ID.value = selectedMiners.value[0].id
    minerPreset2ID.value = selectedMiners.value[1].id
    minerPreset3ID.value = selectedMiners.value.length === 3 ? selectedMiners.value[2].id : undefined
  } else {
    selectedMinerBulkAction.value = command
    minerActionsDialogOpen.value = true
  }
}

/**
 * Handle work order miners export.
 *
 * @param workOrderNumber work order number for which to export miners.
 */
const handleExport = async () => {
  isLoadingExport.value = true
  try {
    try {
      const result = await getWorkOrderDashboardMinersExportCSV(props.number, filters)
      downloadFile(result, `${props.number}_dashboard_miners_export.csv`)
    } catch (err: unknown) {
      if (err instanceof AdminPortalAPIError && err.status === 404) {
        NotifyFail('No miners found for the provided filters')
      } else {
        NotifyFail('Unexpected error when exporting miners.')
      }
    }
  } finally {
    isLoadingExport.value = false
  }
}

/**
 * On mount: fetch miner status and populate status filter.
 */
onMounted(async () => {
  try {
    const [minerStatusData, partStatusData] = await Promise.all([getAllMinerStatuses(), getAllPartStatuses()])

    if (minerStatusData) {
      minerStatusFilterOptions.value = minerStatusData
    }

    if (partStatusData) {
      partStatusFilterOptions.value.push(...partStatusData)
    }

    // Populate selected miner models from URl params
    if (minerModelsSearchParam.value) {
      const models = minerModelsSearchParam.value.split(',')
      selectedMinerModels.value = models.map((model) => ({
        family: '',
        family_abbreviation: '',
        manufacturer: '',
        name: model,
      }))
    }
  } catch {
    NotifyFail('Failed to fetch resources.')
  }
})
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="() => refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <rl-error-result v-if="woSummaryIsError && !woSummaryIsPending" :error="woSummaryError" />
  <template v-else>
    <div class="miners-table__controls">
      <div class="miners-table__filters">
        <!-- First 3 filters always visible -->
        <el-input
          v-model="fragmentFilter"
          style="width: 200px"
          placeholder="SN or MAC"
          clearable
          :suffix-icon="Search"
        />
        <el-select
          v-model="filters.miner_status"
          style="width: 200px"
          placeholder="Miner Status"
          clearable
          @change="handleMinerStatusFilterChange"
        >
          <el-option v-for="status in minerStatusFilterOptions" :key="status" :label="status" :value="status" />
        </el-select>
        <RlMinerModelSelect
          v-model="selectedMinerModels"
          placeholder="Miner Models"
          collapse-tags
          collapse-tags-tooltip
          :max-collapse-tags="0"
          clearable
          @change="handleMinerModelSelectChange"
        />
        <!-- More Filters Popover -->
        <el-popover
          placement="bottom-start"
          width="420"
          trigger="click"
          persistent
          :visible="moreFiltersVisible"
          @before-leave="() => false"
        >
          <template #reference>
            <el-button @click="moreFiltersVisible = !moreFiltersVisible">More Filters</el-button>
          </template>
          <div class="flex flex-col gap-2 min-w-[260px]">
            <div class="flex items-center">
              <span class="inline-block min-w-[100px] text-right mr-2 text-xs text-text-tertiary">Shipping Status</span>
              <el-select
                v-model="filters.miner_shipping_status"
                style="width: 272px"
                placeholder="Shipping Status"
                clearable
                @change="handleMinerShippingStatusFilterChange"
              >
                <el-option
                  v-for="status in minerShippingStatusFilterOptions"
                  :key="status"
                  :label="status"
                  :value="status"
                />
              </el-select>
            </div>
            <div class="flex items-center">
              <span class="inline-block min-w-[100px] text-right mr-2 text-xs text-text-tertiary">Inbound Cont. #</span>
              <el-select
                v-model="filters.inbound_container_number"
                style="width: 272px"
                placeholder="Inbound Container"
                clearable
                @change="handleInboundContainerFilterChange"
              >
                <el-option
                  v-for="container in inboundContainerFilterOptions"
                  :key="container"
                  :label="container"
                  :value="container"
                />
              </el-select>
            </div>
            <div class="flex items-center">
              <span class="inline-block min-w-[100px] text-right mr-2 text-xs text-text-tertiary"
                >Outbound Cont. #</span
              >
              <el-select
                v-model="filters.outbound_container_number"
                style="width: 272px"
                placeholder="Outbound Container"
                clearable
                @change="handleOutboundContainerFilterChange"
              >
                <el-option
                  v-for="container in outboundContainerFilterOptions"
                  :key="container"
                  :label="container"
                  :value="container"
                />
              </el-select>
            </div>
            <div class="flex items-center">
              <span class="inline-block min-w-[100px] text-right mr-2 text-xs text-text-tertiary">Missing Service</span>
              <el-select
                v-model="filters.missing_service"
                style="width: 272px"
                placeholder="Missing Service"
                clearable
                @change="handleMissingServicesFilterChange"
              >
                <el-option v-for="svc in scopeOfWork" :key="svc.id" :label="svc.description" :value="svc.description">
                  <div class="flex items-center gap-3">
                    <Icon :icon="svc.icon" :height="20" />
                    <span>{{ svc.description }}</span>
                  </div>
                </el-option>
              </el-select>
            </div>
            <div class="flex items-center">
              <span class="inline-block min-w-[100px] text-right mr-2 text-xs text-text-tertiary">Part Type</span>
              <el-select
                v-model="partFilter"
                style="width: 272px"
                placeholder="Part Type"
                clearable
                @change="handlePartFilterChange"
              >
                <el-option v-for="opt in partFilterOptions" :key="opt.key" :label="opt.key" :value="opt.value" />
              </el-select>
            </div>
            <div v-if="partFilter" class="flex items-center">
              <span class="inline-block min-w-[100px] text-right mr-2 text-xs text-text-tertiary">Part Status</span>
              <el-select
                v-model="partStatusFilter"
                style="width: 180px"
                placeholder="Part Status"
                clearable
                @change="handlePartStatusFilterChange"
              >
                <el-option v-for="status in partStatusFilterOptions" :key="status" :label="status" :value="status" />
              </el-select>
            </div>
            <div class="flex justify-center mt-2">
              <el-button type="primary" size="small" @click="moreFiltersVisible = false">Close</el-button>
            </div>
          </div>
        </el-popover>
        <el-button v-if="hasFilters" @click="clearAllFilters">Clear filters</el-button>
      </div>
      <div class="miners-table__controls">
        <el-dropdown v-if="isManageableByCurrentUser" @command="handleMinerActions">
          <el-button>Bulk Actions</el-button>
          <template #dropdown>
            <el-dropdown-item :command="MinerBulkActions.StatusUpdate">{{
              MinerBulkActions.StatusUpdate
            }}</el-dropdown-item>
            <el-dropdown-item
              v-if="selectedMiners.length > 0"
              :command="MinerBulkActions.OutboundContainerAssignment"
              >{{ MinerBulkActions.OutboundContainerAssignment }}</el-dropdown-item
            >
            <el-dropdown-item :command="MinerBulkActions.CompleteServices">{{
              MinerBulkActions.CompleteServices
            }}</el-dropdown-item>
            <el-dropdown-item
              v-if="selectedMiners.length >= 2 && selectedMiners.length <= 3"
              :command="MinerBulkActions.Consolidate"
              >{{ MinerBulkActions.Consolidate }}</el-dropdown-item
            >
            <el-dropdown-item
              v-if="isRcAppManagerUser && selectedMiners.length > 0"
              :command="MinerBulkActions.WorkOrderMovement"
              >{{ MinerBulkActions.WorkOrderMovement }}</el-dropdown-item
            >
          </template>
        </el-dropdown>
        <el-button :loading="isLoadingExport" @click="handleExport">Export</el-button>
      </div>
    </div>
    <el-pagination
      v-if="data && data.total_pages > 0"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      class="miners-table__pagination"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="data.total_pages"
      :total="data.total_records"
      :disabled="isFetching"
      :layout="DEFAULT_PAGINATION_LAYOUT"
      background
      @update:current-page="handlePageChange"
      @update:page-size="handlePageSizeChange"
    />
    <el-table
      v-loading="isFetching"
      :data="data?.data"
      :empty-text="tableEmptyText"
      stripe
      row-class-name="dashboards-miners-table-row"
      @row-click="onRowClick"
      @selection-change="handleSelectionChange"
    >
      <el-table-column v-if="isManageableByCurrentUser" type="selection" width="55" />
      <el-table-column prop="model" label="Miner Model" sortable fixed :min-width="240" />
      <el-table-column prop="sn" label="Miner SN" sortable fixed :min-width="280">
        <template #default="scope">
          <CopyButton :value="scope.row.sn">
            <RcDisplaySerialNumber :sn="scope.row.sn" :recurrent="scope.row.has_been_in_rc_before" type="miner" />
          </CopyButton>
        </template>
      </el-table-column>
      <el-table-column prop="mac_address" label="MAC Address" :min-width="168" />
      <el-table-column label="Performed Services" :min-width="168">
        <template #default="scope">
          <div class="flex flex-row items-center gap-2">
            <el-tooltip
              v-for="svc in scope.row.performed_services"
              :key="svc.id"
              effect="dark"
              :content="svc.description"
              placement="top-start"
            >
              <Icon :icon="svc.icon" :width="20" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      <el-table-column :min-width="64">
        <template #default="scope">
          <div class="flex items-center justify-center gap-2">
            <el-tooltip
              v-if="scope.row.is_assigned_to_outbound_container && !scope.row.is_shipped && !scope.row.is_delivered"
              effect="dark"
              content="Assigned to outbound container"
              placement="top-start"
            >
              <Icon icon="material-symbols:package-2" height="20" class="cursor-help" />
            </el-tooltip>
            <el-tooltip v-if="scope.row.is_shipped" effect="dark" content="Shipped" placement="top-start">
              <Icon icon="material-symbols:local-shipping-rounded" height="20" class="cursor-help" />
            </el-tooltip>
            <el-tooltip v-if="scope.row.is_delivered" effect="dark" content="Delivered" placement="top-start">
              <Icon icon="material-symbols:home-rounded" height="20" class="cursor-help" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="Status" :min-width="240">
        <template #default="scope">
          <el-tag class="miner-status-badge">{{ scope.row.status }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="PSU" :min-width="64">
        <template #default="scope">
          <PartStatusIcon :part-state="scope.row.power_supply" />
        </template>
      </el-table-column>
      <el-table-column label="CB" :min-width="64">
        <template #default="scope">
          <PartStatusIcon :part-state="scope.row.control_board" />
        </template>
      </el-table-column>
      <el-table-column label="FAN1" :min-width="64">
        <template #default="scope">
          <PartStatusIcon :part-state="scope.row.fans.find((fan: NumberedPartState) => fan.number === 1)" />
        </template>
      </el-table-column>
      <el-table-column label="FAN2" :min-width="64">
        <template #default="scope">
          <PartStatusIcon :part-state="scope.row.fans.find((fan: NumberedPartState) => fan.number === 2)" />
        </template>
      </el-table-column>
      <el-table-column label="FAN3" :min-width="64">
        <template #default="scope">
          <PartStatusIcon :part-state="scope.row.fans.find((fan: NumberedPartState) => fan.number === 3)" />
        </template>
      </el-table-column>
      <el-table-column label="FAN4" :min-width="64">
        <template #default="scope">
          <PartStatusIcon :part-state="scope.row.fans.find((fan: NumberedPartState) => fan.number === 4)" />
        </template>
      </el-table-column>
      <el-table-column label="HB0" :min-width="64">
        <template #default="scope">
          <PartStatusIcon :part-state="scope.row.hashboards.find((hb: NumberedPartState) => hb.number === 0)" />
        </template>
      </el-table-column>
      <el-table-column label="HB1" :min-width="64">
        <template #default="scope">
          <PartStatusIcon :part-state="scope.row.hashboards.find((hb: NumberedPartState) => hb.number === 1)" />
        </template>
      </el-table-column>
      <el-table-column label="HB2" :min-width="64">
        <template #default="scope">
          <PartStatusIcon :part-state="scope.row.hashboards.find((hb: NumberedPartState) => hb.number === 2)" />
        </template>
      </el-table-column>
      <el-table-column label="HB3" :min-width="64">
        <template #default="scope">
          <PartStatusIcon :part-state="scope.row.hashboards.find((hb: NumberedPartState) => hb.number === 3)" />
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      v-if="data && data.total_pages > 0"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      class="miners-table__pagination"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="data.total_pages"
      :total="data.total_records"
      :disabled="isFetching"
      :layout="DEFAULT_PAGINATION_LAYOUT"
      background
      @update:current-page="handlePageChange"
      @update:page-size="handlePageSizeChange"
    />
    <MinerActionsDialog
      v-if="selectedMinerBulkAction && woSummary"
      v-model="minerActionsDialogOpen"
      :action="selectedMinerBulkAction"
      :miners="selectedMiners"
      :selected-miners-amount="selectedMinersAmount"
      :work-order-number="props.number"
      :work-order-customer-id="woSummary.work_order_information.customer.id"
      :filters="filters"
    />
    <MinersConsolidationDialog
      v-if="woSummary"
      v-model="minerConsolidationDialogOpen"
      :work-order="props.number"
      :customer-id="woSummary.work_order_information.customer.id.toString()"
      :preset1="minerPreset1ID"
      :preset2="minerPreset2ID"
      :preset3="minerPreset3ID"
    />
  </template>
</template>

<style scoped lang="scss">
:deep(.dashboards-miners-table-row) {
  cursor: pointer;
}

.miner-status-badge {
  padding: var(--spacing-spacing-sm);
  border-radius: var(--spacing-spacing-2xl);
  color: var(--text-badge-gray);
  border-color: var(--component-stroke-gray);
  background-color: var(--component-fill-gray);
  white-space: normal;
  overflow-wrap: break-word;
}

.miners-table {
  &__controls {
    display: inline-flex;
    justify-content: space-between;
  }

  &__pagination {
    align-self: flex-end;
  }

  &__filters,
  &__controls {
    display: flex;
    gap: var(--spacing-spacing-sm);
  }
}
</style>
